import type { ShzErrorCause } from '#backend/contract';
import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormField, MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FileInputDirective, FileInputValidators } from '@ngx-dropzone/cdk';
import { MatDropzone } from '@ngx-dropzone/material';
import { EnhancedLoadingService } from '../../services/enhanced-loading.service';
import { SmartOrpcClientService } from '../../services/smart-orpc-client.service';
import { SnackbarService } from '../../services/snackbar.service';
import { isDefinedError, ORPCError } from '@orpc/client';
import { downloadBlobWithAnchor } from '../../utils/file-download';

export interface UploadDisplayConfig {
  showTitle?: boolean;
  showDescription?: boolean;
  titlePosition?: 'above' | 'side' | 'none';
  titleBold?: boolean;
  customTitle?: string;
  customDescription?: string;
  customDescriptionUpload?: string;
  showFileTypeSelector?: boolean;
  enabledFileTypes?: ('shapefile' | 'kml' | 'spreadsheet')[];
  module?: 'compliance' | 'history' | 'monitoring';
}

export type FileType = 'shapefile' | 'kml' | 'spreadsheet';

const shzMimeType =
  '.zip,application/x-zip-compressed,application/zip,application/x-shapefile,.shz';
const kmlMimeType =
  '.kml,.kmz,application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz';

const FILE_TYPE_CONFIG = {
  shapefile: {
    label: 'Shapefile',
    mimeTypes: shzMimeType,
    description: 'Arquivo Shapefile compactado (.zip)',
  },
  kml: {
    label: 'KML/KMZ (Google Earth)',
    mimeTypes: kmlMimeType,
    description: 'Arquivo KML ou KMZ do Google Earth',
  },
  spreadsheet: {
    label: 'Planilha',
    mimeTypes: '.xlsx,.xls',
    description: 'Planilha Excel',
  },
};
@Component({
  selector: 'app-upload-shapefile',
  imports: [
    CommonModule,
    FileInputDirective,
    FormsModule,
    MatChipsModule,
    MatDividerModule,
    MatDropzone,
    MatFormField,
    MatFormFieldModule,
    MatFormFieldModule,
    MatIcon,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatTooltipModule,
    ReactiveFormsModule,
  ],
  templateUrl: './upload-shapefile.component.html',
  styleUrl: './upload-shapefile.component.scss',
})
export class UploadShapefileComponent implements OnInit {
  orpc = inject(SmartOrpcClientService);
  loadingService = inject(EnhancedLoadingService);
  private snackBar = inject(SnackbarService);
  // Inputs para customização
  acceptFile = input<string>(shzMimeType);
  maxSizeMB = input<number>(20);
  requestId = input<number>();
  displayConfig = input<UploadDisplayConfig>({
    showTitle: true,
    showDescription: true,
    titlePosition: 'side',
    titleBold: true,
    customTitle: 'Áreas de interesse',
    customDescription:
      'Efetue o upload de um arquivo Shapefile compactado (.zip)',
    customDescriptionUpload: 'Clique aqui ou arraste arquivo para fazer upload',
    showFileTypeSelector: false,
    enabledFileTypes: ['shapefile'],
  });

  // Outputs
  fileUploaded = output<string | null>();
  fileSelected = output<File | null>();
  uploadStarted = output<void>();
  uploadCompleted = output<string>();
  uploadError = output<Error>();
  originalFileName = output<string | null>(); // Nome original do arquivo antes do upload
  fileTypeChanged = output<FileType>();

  fileCtrl = new FormControl<File | null>(null);
  selectedFileType = signal<FileType>('spreadsheet');
  isDownloadingTemplate = signal<boolean>(false);

  // Armazenar nome original do arquivo
  private _originalFileName: string | null = null;

  // Propriedades computadas
  get config() {
    return this.displayConfig();
  }

  get file() {
    return this.fileCtrl.value ?? null;
  }

  get isUploading() {
    return this.loadingService.button$;
  }

  get enabledFileTypes() {
    return this.config.enabledFileTypes || ['spreadsheet'];
  }

  get currentFileTypeConfig() {
    return FILE_TYPE_CONFIG[this.selectedFileType()];
  }

  readonly FILE_TYPE_CONFIG = FILE_TYPE_CONFIG;
  get currentAcceptTypes() {
    return this.config.showFileTypeSelector
      ? this.currentFileTypeConfig.mimeTypes
      : this.acceptFile();
  }

  get currentDescription(): string {
    return (
      this.config.customDescription ||
      'Selecione o tipo de arquivo e faça o upload das áreas que deseja analisar'
    );
  }

  /**
   * Retorna o tamanho máximo permitido para o tipo de arquivo selecionado
   * - Shapefile e KML: 20MB
   * - Excel: 2MB
   */
  get currentMaxSizeMB(): number {
    if (this.config.showFileTypeSelector) {
      const fileType = this.selectedFileType();
      switch (fileType) {
        case 'spreadsheet':
          return 2; // Excel: 2MB
        case 'kml':
        case 'shapefile':
        default:
          return 20; // KML e Shapefile: 20MB
      }
    }
    // Modo legado: usar maxSizeMB do input
    return this.maxSizeMB();
  }

  constructor() {
    // Atualizar validadores quando os inputs mudarem
    this.setupValidators();
  }

  ngOnInit(): void {
    // Definir tipo inicial baseado nos tipos habilitados
    // Preferir planilha como padrão quando disponível
    const enabledTypes = this.enabledFileTypes;
    if (enabledTypes.length > 0) {
      const defaultType = enabledTypes.includes('spreadsheet')
        ? 'spreadsheet'
        : enabledTypes[0];
      this.selectedFileType.set(defaultType);
    }

    this.setupValidators();
    this.fileCtrl.valueChanges.subscribe(() => this.onFileSelected());

    // Emitir tipo inicial para que componentes irmãos já partam do estado correto
    this.fileTypeChanged.emit(this.selectedFileType());
  }

  onFileTypeChange(): void {
    // Limpar arquivo anterior ao trocar tipo
    this.remove();

    // Atualizar validadores
    this.setupValidators();

    // Notificar componentes irmãos sobre a mudança de tipo
    this.fileTypeChanged.emit(this.selectedFileType());
  }

  private setupValidators() {
    const acceptTypes = this.currentAcceptTypes;
    const maxSize = this.currentMaxSizeMB * 1024 * 1024; // converter MB para bytes
    console.log('Validadores atualizados:', {
      acceptTypes,
      maxSizeMB: this.currentMaxSizeMB,
      fileType: this.selectedFileType(),
    });
    this.fileCtrl.setValidators([
      FileInputValidators.accept(acceptTypes),
      FileInputValidators.maxSize(maxSize),
    ]);
    this.fileCtrl.updateValueAndValidity();
  }

  readonly errorCodes = {
    fileType: FileInputValidators.accept.name,
    maxSize: FileInputValidators.maxSize.name,
    geometryType: 'geometryType',
  } as const;

  readonly unprocessableContentMessagesPtBr: Record<ShzErrorCause, string> = {
    zipError: `Falha ao descompactar o arquivo zip. O arquivo pode estar corrompido ou inválido.`,
    driverError:
      'Erro ao abrir arquivo: O arquivo de entrada deverá ser em formato shapefile (.shp, .dbf, .shx, .prj) compactado .zip.',
    missingFiles: `O arquivo .zip está incompleto ou inválido. O arquivo de entrada deverá ser em formato shapefile (.shp, .dbf, .shx, .prj) compactado .zip`,
    recordCountMismatch:
      'O arquivo shapefile não é válido. Os registros do DBF são diferentes do número de polígonos',
  };

  readonly unprocessableContentReasons = Object.keys(
    this.unprocessableContentMessagesPtBr,
  ) as ShzErrorCause[];

  private onFileSelected(): void {
    console.log('Arquivo selecionado:', this.file);
    console.log('Arquivo selecionado:', this.fileCtrl);
    if (this.file && this.fileCtrl.valid) {
      this.fileSelected.emit(this.file);
      this.processFile(this.file);
    }
  }

  private async processFile(file: File): Promise<void> {
    this.uploadStarted.emit();
    this.loadingService.show('button');

    // Capturar e armazenar o nome original do arquivo ANTES do upload
    this._originalFileName = file.name;
    this.originalFileName.emit(this._originalFileName);

    try {
      let fileName: string;

      // Escolher o endpoint e validação corretos baseado no tipo de arquivo
      if (this.config.showFileTypeSelector) {
        const fileType = this.selectedFileType();
        switch (fileType) {
          case 'kml':
            fileName = await this.uploadKML(file);
            break;
          case 'shapefile':
            fileName = await this.uploadShapefile(file);
            break;
          case 'spreadsheet':
            fileName = await this.uploadExcel(file);
            break;
          default:
            throw new Error(`Tipo de arquivo não suportado: ${fileType}`);
        }
      } else {
        // Lógica original para compatibilidade (sem seletor de tipo)
        fileName = await this.uploadLegacyFile(file);
      }

      // Se fileName vazio, significa que houve erro de validação (não fazer emit)
      if (!fileName) {
        return;
      }

      this.fileUploaded.emit(fileName);
      this.uploadCompleted.emit(fileName);
    } catch (error) {
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      this.uploadError.emit(errorObj);
      // Erros de validação puramente locais (ex: geometria inválida) não passam pelo
      // interceptor do orpc, que só alerta erros vindos das chamadas ao backend
      if (!(error instanceof ORPCError)) {
        this.snackBar.alert(errorObj.message);
      }
      throw error;
    } finally {
      this.loadingService.hide('button');
    }
  }

  /**
   * Upload e validação de arquivo KML/KMZ
   */
  private async uploadKML(file: File): Promise<string> {
    // Upload do arquivo KML
    const { data: res, error } = await this.orpc.safeClient.storage.uploadKML({
      file,
    });

    if (!error) {
      // Buscar metadados do KML
      const metadata = await this.orpc.client.storage.getKMLMetadata({
        fileName: res.fileName,
      });

      // Validar se é do Google Earth
      if (!metadata.isFromGoogleEarth) {
        this.fileCtrl.setErrors({
          ...this.fileCtrl.errors,
          notGoogleEarth: true,
        });
        throw new Error('Arquivo não foi criado no Google Earth');
      }

      if (metadata.confidence === 'low') {
        this.fileCtrl.setErrors({
          ...this.fileCtrl.errors,
          lowConfidence: true,
        });
      }

      // Validar tipo de geometria (apenas Polygon/MultiPolygon)
      if (
        !metadata.geomTypes.every((t) => t.includes('Polygon') || t === 'None')
      ) {
        this.fileCtrl.setErrors({
          ...this.fileCtrl.errors,
          [this.errorCodes.geometryType]: true,
        });
        throw new Error(
          'Arquivo contém geometrias diferentes de Polygon/MultiPolygon',
        );
      }

      return res.fileName;
    } else {
      // Capturar erros específicos do backend
      if (isDefinedError(error)) {
        const reason = error.data.reason;

        // Mapear códigos de erro do backend para códigos do frontend
        switch (reason) {
          case 'fileTooLarge':
            this.fileCtrl.setErrors({
              ...this.fileCtrl.errors,
              [this.errorCodes.maxSize]: true,
            });
            break;
          case 'notGoogleEarth':
            this.fileCtrl.setErrors({
              ...this.fileCtrl.errors,
              notGoogleEarth: true,
            });
            break;
          case 'lowConfidence':
            this.fileCtrl.setErrors({
              ...this.fileCtrl.errors,
              lowConfidence: true,
            });
            break;
          case 'invalidGeometry':
            this.fileCtrl.setErrors({
              ...this.fileCtrl.errors,
              [this.errorCodes.geometryType]: true,
            });
            break;
          case 'invalidKML':
          case 'kmzNoKML':
          case 'kmzError':
          case 'kmlReadError':
          case 'gdalError': {
            const errorMessage =
              (error as any).message || 'Erro ao processar arquivo KML/KMZ';
            this.fileCtrl.setErrors({
              ...this.fileCtrl.errors,
              kmlError: {
                reason,
                message: errorMessage,
              },
            });
            break;
          }
        }
      }
      // Re-lançar erro para ser capturado pelo catch do processFile
      throw error;
    }
  }

  /**
   * Upload e validação de arquivo Shapefile
   */
  private async uploadShapefile(file: File): Promise<string> {
    const res = await this.orpc.client.storage.upload({ file });

    // Validar metadados do shapefile
    const {
      error,
      data: meta,
      isDefined,
    } = await this.orpc.safeClient.storage.getShpMetadata({
      fileName: res.fileName,
    });

    if (error && !isDefined) {
      throw error;
    } else if (error && isDefined) {
      const errorCode: ShzErrorCause = error.data?.reason;
      this.fileCtrl.setErrors({
        ...this.fileCtrl.errors,
        [errorCode]: true,
      });
      throw error;
    }

    // Validar tipo de geometria (apenas Polygon)
    if (!meta?.geomTypes.every((t) => t.includes('Polygon'))) {
      this.fileCtrl.setErrors({
        ...this.fileCtrl.errors,
        [this.errorCodes.geometryType]: true,
      });
      throw new Error('Arquivo contém geometrias diferentes de Polygon');
    }

    return res.fileName;
  }

  /**
   * Upload e validação de arquivo Excel (Planilha)
   */
  private async uploadExcel(file: File): Promise<string> {
    // 1. Upload do arquivo Excel para o storage
    const { data: uploadResponse, error } =
      await this.orpc.safeClient.storage.uploadExcel({
        file,
      });

    if (!error) {
      const fileName = uploadResponse.fileName;

      // 2. Buscar metadados e validar arquivo já salvo no storage
      const metadata = await this.orpc.client.requestTemplate.validate({
        fileName,
        module: this.config.module || 'compliance',
      });

      // 3. Verificar se a validação passou
      if (!metadata.valid) {
        // Sempre usar erro agregado (não mostrar linha a linha)
        const aggregatedError = metadata.aggregatedError;

        if (aggregatedError) {
          // Construir resumo dos campos com problema
          const fieldsSummary = aggregatedError.fields
            .map((f) => `${f.field} (${f.invalidRows} linhas)`)
            .join(', ');

          this.fileCtrl.setErrors({
            ...this.fileCtrl.errors,
            excelValidation: {
              aggregated: true,
              message: aggregatedError.message,
              summary: aggregatedError,
              fieldsSummary,
            },
          });

          return '';
        }

        // Se não houver erro agregado, criar mensagem genérica
        this.fileCtrl.setErrors({
          ...this.fileCtrl.errors,
          excelValidation: {
            aggregated: true,
            message: 'O arquivo informado não possui informações válidas',
            summary: null,
          },
        });

        return '';
      }

      // 4. Arquivo válido - retornar fileName do storage
      return fileName;
    } else {
      // Capturar erros específicos do backend (ex: arquivo muito grande)
      if (isDefinedError(error)) {
        if (error.data.reason === 'fileTooLarge') {
          this.fileCtrl.setErrors({
            ...this.fileCtrl.errors,
            [this.errorCodes.maxSize]: true,
          });
        }
      }

      // Re-lançar erro para ser capturado pelo catch do processFile
      throw error;
    }
  }

  /**
   * Baixa o template Excel para preenchimento
   * @param module Módulo do template (compliance, history, monitoring)
   */
  async downloadExcelTemplate(): Promise<void> {
    this.isDownloadingTemplate.set(true);

    try {
      const response = await this.orpc.client.requestTemplate.download({
        module: this.config.module || 'compliance',
      });

      downloadBlobWithAnchor(response.blob, response.filename);
    } finally {
      this.isDownloadingTemplate.set(false);
    }
  }

  /**
   * Upload legado (sem seletor de tipo de arquivo)
   * Mantido para compatibilidade com código existente
   */
  private async uploadLegacyFile(file: File): Promise<string> {
    let res: { fileName: string };

    // Detectar tipo de arquivo pelo acceptFile
    switch (true) {
      case this.acceptFile().includes('dbf'):
        res = await this.orpc.client.storage.uploadDbf({ file });
        break;
      case this.acceptFile().includes('fgb'):
        res = await this.orpc.client.storage.uploadFgb({ file });
        break;
      default:
        res = await this.orpc.client.storage.upload({ file });
        break;
    }

    // Validação após upload
    await this.validateUploadedFile(res.fileName);

    return res.fileName;
  }

  /**
   * Validação legada (para modo sem seletor de tipo)
   */

  private async validateUploadedFile(fileName: string): Promise<void> {
    const requestId = this.requestId();

    // Validação baseada no tipo de arquivo
    // FIXME: nao faz sentido chavear essa validação pelo formato do arquivo, a função de validação precisa ser fornecida pelo componente pai e ser usada como callback
    if (fileName.endsWith('.zip') || fileName.endsWith('.shz')) {
      const {
        error,
        data: meta,
        isDefined,
      } = await this.orpc.safeClient.storage.getShpMetadata({
        fileName,
      });
      if (error && !isDefined) {
        throw error;
      } else if (error && isDefined) {
        const errorCode: ShzErrorCause = error.data?.reason;
        this.fileCtrl.setErrors({
          ...this.fileCtrl.errors,
          [errorCode]: true,
        });
        return;
      }

      if (!meta?.geomTypes.every((t) => t.includes('Polygon'))) {
        this.fileCtrl.setErrors({
          ...this.fileCtrl.errors,
          [this.errorCodes.geometryType]: true,
        });
      }
    } else if (fileName.endsWith('.dbf') && requestId) {
      const validation =
        await this.orpc.client.historiesSafras.validateHarvestHistoryFile({
          fileName,
          requestId,
        });
      if (!validation.isValid) {
        this.fileCtrl.setErrors({
          ...this.fileCtrl.errors,
          dbfValidation: {
            missingFields: validation.missingFields,
            validationErrors: validation.validationErrors,
            error: validation.error,
          },
        });
      }
    } else if (fileName.endsWith('.fgb') && requestId) {
      const validation =
        await this.orpc.client.historiesSafras.validateLandUseFile({
          fileName,
          requestId,
        });
      if (!validation.isValid) {
        this.fileCtrl.setErrors({
          ...this.fileCtrl.errors,
          fgbValidation: {
            missingFields: validation.missingFields,
            validationErrors: validation.validationErrors,
            error: validation.error,
          },
        });
      }
    }
  }

  remove() {
    this.fileCtrl.setValue(null);
    this.fileSelected.emit(null);
    this.fileUploaded.emit(null);
    this._originalFileName = null;
    this.originalFileName.emit(null);
  }

  // Método para obter mensagem de erro baseada no tipo de arquivo aceito
  getAcceptErrorMessage(): string {
    const acceptTypes = this.currentAcceptTypes;

    // Mapear tipos MIME comuns para mensagens amigáveis
    const typeMessages: Record<string, string> = {
      [shzMimeType]: 'Por favor, envie um arquivo .zip ou .shz',
      [kmlMimeType]: 'Por favor, envie um arquivo .kml ou .kmz',
      '.xlsx,.xls': 'Por favor, envie um arquivo .xlsx ou .xls',
      '.dbf': 'Por favor, envie um arquivo .dbf',
      '.fgb': 'Por favor, envie um arquivo .fgb ou .shz',
    };

    // Verificar se existe mensagem específica para o tipo
    if (typeMessages[acceptTypes]) {
      return typeMessages[acceptTypes];
    }

    // Gerar mensagem genérica baseada nas extensões
    const extensions = acceptTypes
      .split(',')
      .map((type) => type.trim())
      .filter((type) => type.startsWith('.'))
      .join(', ');

    if (extensions) {
      return `Por favor, envie um arquivo com extensão: ${extensions}`;
    }

    // Fallback genérico
    return `Tipo de arquivo não aceito. Tipos aceitos: ${acceptTypes}`;
  }
}
